import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

export interface IToastAction {
  label: string;
  onClick: () => void;
}

export interface IToast {
  id: number;
  title?: string;
  message: string;
  type: ToastType;
  duration: number;
  action?: IToastAction;
}

type ToastOptions = Partial<Pick<IToast, 'title' | 'duration' | 'action'>>;

const DURACAO_COMPACTO = 3200;
const DURACAO_EXPANDIDO = 6000;

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private toastsSubject = new BehaviorSubject<IToast[]>([]);
  toasts$ = this.toastsSubject.asObservable();
  private idCounter = 0;

  show(toast: Omit<IToast, 'id' | 'duration'> & { duration?: number }): void {
    const id = ++this.idCounter;
    const duration = toast.duration ?? this.duracaoPadrao(toast);
    this.toastsSubject.next([...this.toastsSubject.value, { ...toast, id, duration }]);
  }

  success(message: string, options: ToastOptions = {}) {
    this.show({ message, type: 'success', ...options });
  }

  error(message: string, options: ToastOptions = {}) {
    this.show({ message, type: 'error', ...options });
  }

  info(message: string, options: ToastOptions = {}) {
    this.show({ message, type: 'info', ...options });
  }

  warning(message: string, options: ToastOptions = {}) {
    this.show({ message, type: 'warning', ...options });
  }

  close(id: number): void {
    this.toastsSubject.next(
      this.toastsSubject.value.filter((t) => t.id !== id)
    );
  }

  clear(): void {
    this.toastsSubject.next([]);
  }

  private duracaoPadrao({ type, title, action }: Pick<IToast, 'type' | 'title' | 'action'>): number {
    if (action || type === 'error') {
      return 0;
    }
    return title ? DURACAO_EXPANDIDO : DURACAO_COMPACTO;
  }
}
